/**
 * The boot descriptor: `GET /api/v1/config` of the primary chain, loaded once
 * before the router mounts (main.tsx).
 *
 * Nothing about a deployment is baked into the bundle, and nothing is assumed
 * when the descriptor is silent: an unknown chain id, network or explorer is
 * `null`, never a default network's.
 *
 * The pages read their networks from the session; this one names the single
 * chain of a deployment without `/chains.json`, and the WalletConnect project
 * id (`walletConnect.projectId`): none means WalletConnect is not offered.
 * `loadRuntimeConfig()` never rejects: an unreachable or 404 gateway leaves the
 * cache empty, and callers degrade.
 */

import { api, type ConfigResponse, type ConfigTokenResponse } from "./api";

export type TokenKind = "erc20" | "fiat";

export interface RuntimeToken {
  symbol: string;
  name: string | null;
  /** Base-unit decimals per the deployed contract; null when unknown. */
  decimals: number | null;
  /** Deployed address, or null when the deployment has not provisioned it. */
  address: string | null;
  kind: TokenKind;
}

export interface RuntimeConfig {
  /** null when the descriptor names none. */
  chainId: number | null;
  /** "base", "bsc"; null when the descriptor names none. */
  network: string | null;
  contracts: { lop: string | null; settlement: string | null };
  tokens: RuntimeToken[];
  bookTokens?: RuntimeToken[];
  poolPairs?: string[];
  platformFee?: ConfigResponse["platformFee"];
  /** Without a trailing slash; null when the descriptor names none. */
  explorerBaseUrl: string | null;
  /** `projectId` null = WalletConnect is not offered at all. */
  walletConnect: { projectId: string | null };
}

/** An address is only usable if it looks like one: anything else is "unknown". */
function readAddress(value: unknown): string | null {
  return typeof value === "string" && /^0x[0-9a-fA-F]{40}$/.test(value) ? value.toLowerCase() : null;
}

function readWalletConnectProjectId(raw: ConfigResponse): string | null {
  const block: unknown = raw.walletConnect;
  if (block === null || typeof block !== "object") return null;
  const projectId = (block as { projectId?: unknown }).projectId;
  return typeof projectId === "string" && projectId !== "" ? projectId : null;
}

function readToken(raw: ConfigTokenResponse): RuntimeToken | null {
  if (typeof raw.symbol !== "string" || raw.symbol === "") return null;
  return {
    symbol: raw.symbol,
    name: typeof raw.name === "string" ? raw.name : null,
    decimals: typeof raw.decimals === "number" && Number.isInteger(raw.decimals) && raw.decimals >= 0 ? raw.decimals : null,
    address: readAddress(raw.address),
    kind: raw.kind === "fiat" ? "fiat" : "erc20",
  };
}

/** Folds the optional-everything wire payload into a complete config object. */
export function normalizeConfig(raw: ConfigResponse): RuntimeConfig {
  return {
    chainId: typeof raw.chainId === "number" && Number.isFinite(raw.chainId) ? raw.chainId : null,
    network: typeof raw.network === "string" && raw.network !== "" ? raw.network : null,
    contracts: {
      lop: readAddress(raw.contracts?.lop),
      settlement: readAddress(raw.contracts?.settlement),
    },
    ...(raw.prices?.pairs && typeof raw.prices.pairs === "object" && !Array.isArray(raw.prices.pairs) ? { poolPairs: Object.keys(raw.prices.pairs).filter(pair => /^[A-Za-z0-9_.-]+\/[A-Za-z0-9_.-]+$/.test(pair)).map(pair => pair.toUpperCase()) } : {}),
    ...(Array.isArray(raw.bookTokens) ? { bookTokens: raw.bookTokens.map(readToken).filter((t): t is RuntimeToken => t !== null) } : {}),
    ...(raw.platformFee && ["known", "none", "unknown"].includes(raw.platformFee.state) ? { platformFee: raw.platformFee } : {}),
    tokens: Array.isArray(raw.tokens) ? raw.tokens.map(readToken).filter((t): t is RuntimeToken => t !== null) : [],
    explorerBaseUrl:
      typeof raw.explorerBaseUrl === "string" && raw.explorerBaseUrl !== "" ? raw.explorerBaseUrl.replace(/\/+$/, "") : null,
    walletConnect: { projectId: readWalletConnectProjectId(raw) },
  };
}

let cache: RuntimeConfig | null = null;
let inflight: Promise<RuntimeConfig | null> | null = null;

/**
 * Fetches the descriptor once and caches it. Never rejects: a missing config
 * is a degraded state, not a crash. Repeat calls return the cached value (or
 * join the in-flight request).
 */
export function loadRuntimeConfig(): Promise<RuntimeConfig | null> {
  if (cache !== null) return Promise.resolve(cache);
  if (inflight !== null) return inflight;
  inflight = api
    .config()
    .then((raw) => {
      cache = normalizeConfig(raw);
      return cache;
    })
    .catch(() => {
      // 404 is expected and unreported; other failures were already reported
      // by the api layer. Either way: degrade, don't throw.
      return null;
    })
    .finally(() => {
      inflight = null;
    });
  return inflight;
}

/** Cached config, or null when it has not loaded (or failed to load). */
export function getRuntimeConfig(): RuntimeConfig | null {
  return cache;
}

/** Replaces the cache. Used by tests. */
export function setRuntimeConfig(config: RuntimeConfig | null): void {
  cache = config;
  inflight = null;
}

/** "base" → "Base"; null before the descriptor lands, or when it names no network. */
export function getNetworkLabel(): string | null {
  const network = cache?.network ?? null;
  return network === null ? null : network.charAt(0).toUpperCase() + network.slice(1);
}

/**
 * The WalletConnect project id the boot descriptor publishes, or null when it
 * publishes none, or names no chain id for a session to be proposed for. Null
 * means WalletConnect is not offered at all: never a button that opens a QR
 * sheet and then hangs on a relay that answers 401.
 */
export function getWalletConnectProjectId(): string | null {
  return cache === null || cache.chainId === null ? null : cache.walletConnect.projectId;
}
